import classnames from 'classnames';
import LocalizedLink from 'components/LocalizedLink';
import useStores from 'hooks/useStores';
import { ExperienceSource } from 'model/IExperience';
import React, { useCallback, useMemo } from 'react';
import { useTrekker } from 'utils/analytics';
import { isWeb } from 'utils/platform';
import { useLang } from '~/stores/i18n';

import CSS from './ExperienceLinkContainer.module.scss';

interface ExperienceLinkProps {
  experience: ExperienceSource;
  children?: any;
  className?: string;
  onClick?: () => void;
  ariaHidden?: boolean;
  forceHost?: string;
  preferredLanguage?: string;
  noFocus?: boolean;
  extraSearchParams?: Record<string, string>;
}

function ExperienceLink({
  experience,
  children,
  className,
  onClick,
  ariaHidden,
  forceHost,
  preferredLanguage,
  noFocus,
  extraSearchParams,
  ...rest
}: ExperienceLinkProps) {
  const { nrOfAdults, nrOfChildren, date } = useStores(
    ['FiltersStore'],
    (flux) => ({
      nrOfAdults: flux.stores.FiltersStore.getAdults(),
      nrOfChildren: flux.stores.FiltersStore.getChildren(),
      date: flux.stores.FiltersStore.getFilters().activeFilters.find(
        (f) => f.uid === 'date.from',
      )?.value,
    }),
  );

  const trekker = useTrekker();
  const lang = useLang();

  const useNewTab = isWeb() && window.innerWidth >= 768;

  const to = useMemo(() => {
    const searchUrlParams = new URLSearchParams();

    if (extraSearchParams) {
      Object.keys(extraSearchParams).forEach((key) => {
        searchUrlParams.set(key, extraSearchParams[key]);
      });
    }

    searchUrlParams.set('adults', `${nrOfAdults}`);
    searchUrlParams.set('children', `${nrOfChildren}`);

    if (date) {
      searchUrlParams.set('date', date);
    }

    if (forceHost) {
      searchUrlParams.set('hostId', forceHost);
    }

    if (preferredLanguage && preferredLanguage !== lang) {
      searchUrlParams.set('preferredLanguage', preferredLanguage);
    }

    return {
      pathname: `/experience/${experience.pretty_url}/`,
      search: searchUrlParams.toString(),
      state: {
        experience,
      },
    };
  }, [
    extraSearchParams,
    date,
    experience,
    forceHost,
    nrOfAdults,
    nrOfChildren,
    preferredLanguage,
    lang,
  ]);

  const target = useNewTab ? '_blank' : undefined;
  const handleClick = useCallback(() => {
    trekker.event('click', { linkTo: to });
    onClick?.();
  }, [to, trekker, onClick]);

  return (
    <LocalizedLink
      to={to}
      className={classnames(className, CSS.ExperienceLink)}
      target={target}
      ariaHidden={ariaHidden}
      onClick={handleClick}
      tabIndex={noFocus ? -1 : 0}
      {...rest}
    >
      {children}
    </LocalizedLink>
  );
}

export default ExperienceLink;
